<template>
    <div class="d-flex">
        <Sidebar/>
        <div class="component-container-sidebar">
            <h6 class="m-3">Account ID: {{ creatorInfo.AdAccountId.substring(4) }}</h6>
            <h6 class="ms-3">Campaign ID: {{ selectedCampaign.CampaignId }}</h6>
            <div class="card mb-3 mt-3">
                <div class="card-header">
                    Property Listing
                </div>
                <div class="card-body">
                    <div v-if="!listing">
                        <div class="row">
                            <div class="col-md-5 mb-3">
                                <label for="listingId" class="form-label">Listing ID</label>
                                <div class="input-group">
                                    <input
                                        v-model="listingId"
                                        type="text"
                                        class="form-control"
                                        id="listingId"
                                        placeholder="Enter Listing ID"
                                        :disabled="isLoading"
                                    >
                                    <button
                                        type="button"
                                        class="btn btn-primary"
                                        :disabled="isLoading || !listingId.trim()"
                                        @click="lookupListing"
                                        :style="{ cursor: isLoading ? 'wait' : 'pointer' }"
                                    >
                                        <span v-if="isLoading" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                        Look Up
                                    </button>
                                </div>
                                <small class="text-muted">Domain: {{ creatorInfo.DomainName }}</small>
                            </div>
                        </div>
                    </div>

                    <div v-if="listing">
                        <h6 class="mb-3">Please confirm the property listing below:</h6>
                        <div class="row mb-2">
                            <div class="col-md-6">
                                <strong>Address</strong>
                                <p class="mb-0">
                                    {{ listing.StreetAddress }}<span v-if="listing.UnitNumber"> #{{ listing.UnitNumber }}</span>
                                </p>
                                <p class="mb-0">{{ listing.City }}, {{ listing.State }} {{ listing.PostalCode }}</p>
                            </div>
                            <div class="col-md-3">
                                <strong>Status</strong>
                                <p class="mb-0">{{ listing.Status }}</p>
                            </div>
                            <div class="col-md-3">
                                <strong>Price</strong>
                                <p class="mb-0">{{ listing.Price != null ? '$' + listing.Price.toLocaleString() : 'N/A' }}</p>
                            </div>
                        </div>
                        <div class="row mb-2">
                            <div class="col-md-3">
                                <strong>MLS #</strong>
                                <p class="mb-0">{{ listing.MlsNumber || 'N/A' }}</p>
                            </div>
                            <div class="col-md-3">
                                <strong>Beds</strong>
                                <p class="mb-0">{{ listing.Bedrooms != null ? listing.Bedrooms : 'N/A' }}</p>
                            </div>
                            <div class="col-md-3">
                                <strong>Baths</strong>
                                <p class="mb-0">{{ listing.Bathrooms != null ? listing.Bathrooms : 'N/A' }}</p>
                            </div>
                            <div class="col-md-3">
                                <strong>Sq Ft</strong>
                                <p class="mb-0">{{ listing.SquareFeet != null ? listing.SquareFeet.toLocaleString() : 'N/A' }}</p>
                            </div>
                        </div>
                        <div class="row mb-2">
                            <div class="col-md-3">
                                <strong>Year Built</strong>
                                <p class="mb-0">{{ listing.YearBuilt != null ? listing.YearBuilt : 'N/A' }}</p>
                            </div>
                            <div class="col-md-3">
                                <strong>Provided By</strong>
                                <p class="mb-0">{{ listing.ProvidedBy || 'N/A' }}</p>
                            </div>
                            <div class="col-md-6" v-if="listing.DetailsUrl">
                                <strong>Details URL</strong>
                                <p class="mb-0"><a :href="listing.DetailsUrl" target="_blank" rel="noopener noreferrer">{{ listing.DetailsUrl }}</a></p>
                            </div>
                        </div>
                        <div class="row mb-3" v-if="listing.Notes">
                            <div class="col-md-12">
                                <strong>Notes</strong>
                                <p class="mb-0">{{ listing.Notes }}</p>
                            </div>
                        </div>
                        <div class="row mb-3" v-if="listing.PropertyImageLinks && listing.PropertyImageLinks.length > 0">
                            <div class="col-md-12">
                                <strong>Images ({{ listing.PropertyImageLinks.length }})</strong>
                                <div class="d-flex flex-wrap mt-1">
                                    <img
                                        v-for="(imgUrl, index) in listing.PropertyImageLinks.slice(0, 4)"
                                        :key="index"
                                        :src="imgUrl"
                                        alt="Property image"
                                        class="me-2 mb-2"
                                        style="height: 80px; width: 120px; object-fit: cover; border-radius: 4px;"
                                    />
                                    <span v-if="listing.PropertyImageLinks.length > 4" class="align-self-center text-muted">
                                        +{{ listing.PropertyImageLinks.length - 4 }} more
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div class="mt-3">
                            <button type="button" class="btn btn-primary me-2" @click="confirmListing">Save &amp; Continue</button>
                            <button type="button" class="btn btn-secondary" @click="cancelListing">Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
            <div class="alert alert-danger" role="alert" v-if="displayError">
                {{ errorMessage }}
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { Vue, Component } from 'vue-property-decorator';
import { CreatorInfo } from '../../models/CreatorInfo';
import { ConsumerPropertyListing } from '../../models/ConsumerPropertyListing';
import Sidebar from './Sidebar.vue';

@Component({
    name: 'PropertyListing',
    components: {
        Sidebar
    }
})
export default class PropertyListing extends Vue {

    listingId: string = '';
    isLoading: boolean = false;
    displayError: boolean = false;
    errorMessage: string = '';

    get creatorInfo(): CreatorInfo {
        return this.$store.state.creator.creatorInfo;
    }

    get selectedCampaign() {
        return this.$store.getters['creator/selectedCampaign'];          
    }

    get listing(): ConsumerPropertyListing | null {
        return this.$store.state.creator.activePropertyListing;
    }

    lookupListing() {
        this.displayError = false;
        this.isLoading = true;

        this.$store.dispatch('creator/getPropertyListing', {
            domainName: this.creatorInfo.DomainName,
            listingId: this.listingId.trim()
        })
        .catch((error: any) => {
            this.displayError = true;
            this.errorMessage = `Unable to find property listing. Please check the Listing ID and try again. ${error}`;
        })
        .finally(() => {
            this.isLoading = false;
        });
    }

    confirmListing() {
        this.$router.push('/facebook/creator/adset');
    }

    cancelListing() {
        this.listingId = '';
        this.displayError = false;
        this.errorMessage = '';
        this.$store.dispatch('creator/setActivePropertyListing', null);
    }
}
</script>
